/**
 * QHS diagnostic logger.
 *
 * Tiny helper used to instrument the auth / init / navigation flow
 * so we can capture a timeline when a hard-to-repro bug fires
 * (e.g. the "forever loader after auto-logout" intermittent).
 *
 * Usage in browser DevTools:
 *
 *     // turn on
 *     localStorage.qhsDebug = '1'
 *     // reload, then reproduce the bug
 *     // copy the [QHS_DEBUG] lines from console
 *
 *     // turn off
 *     localStorage.removeItem('qhsDebug')
 *
 * Output format (one line per event):
 *
 *     [QHS_DEBUG] 2026-05-06T18:15:49.264Z auth:login.fulfilled
 *     {ok: true, hasMetadata: true, hasUser: false, ...}
 *
 * The flag is read on every call (not cached) so the user can flip
 * debug on / off without reloading. Calls are no-ops in production
 * unless the flag is set, so this file is safe to ship.
 */

const FLAG_KEY = 'qhsDebug';

/**
 * Internal: cheap localStorage probe with a try/catch so an
 * exotic environment (Safari private mode, iframe with denied
 * storage) can't crash the app.
 */
const isEnabled = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(FLAG_KEY) === '1';
  } catch (e) {
    return false;
  }
};

/**
 * Log a single diagnostic event. `data` is an arbitrary object —
 * pass whatever is most relevant at the call site (request params,
 * before/after state slice, condition outcome).
 */
export const qhsDebug = (event, data) => {
  if (!isEnabled()) return;
  const ts = new Date().toISOString();
  // eslint-disable-next-line no-console
  console.log(`[QHS_DEBUG] ${ts} ${event}`, data ?? '');
};

/**
 * Snapshot the auth + table-settings slices the way the bug-hunting
 * timeline cares about. Used by the Redux debug middleware so every
 * tracked action carries a uniform "what does the world look like"
 * payload alongside its name + payload.
 */
export const qhsSliceSnapshot = state => ({
  isLogin: state?.user?.isLogin,
  user: state?.user?.user?.uuid || null,
  metadata: state?.user?.metadata?.currentAccount?.uuid || null,
  hipaaCheckParams: !!state?.user?.hipaaCheckParams,
  isLoginStatus: state?.user?.isLoginStatus,
  metadataStatus: state?.user?.metadataStatus,
  userStatus: state?.user?.status,
  tableSettings: state?.table?.settings ? Object.keys(state.table.settings).length : null,
  tableStatus: state?.table?.status,
  tableError: state?.table?.error || null,
});

/**
 * True when debug mode is active. Helpful for higher-level call
 * sites that want to skip building a heavy payload when nobody is
 * listening.
 */
export const isQhsDebug = isEnabled;
